<template>
  <div :class="containerClass">
    <!-- selectedFiltersModel: {{ selectedFiltersModel }}  -->

    <template
      v-for="(filter, filterName) in filtersDataFiltered"
      :key="filterName">
      <div
        v-if="showFilters.includes(filterName)"
        class="s-playing-now__options__filters__element">
        <select
          :value="modelValue[filterName]"
          class="form-select"
          :class="{ 'form-select--mobile': !desktop }"
          :disabled="!filter.length"
          @change="(e) => filterOptions(e, filterName)">
          <option value="All" selected>
            {{ equivalentPlaceholder(filterName) }}
          </option>

          <template v-if="filterName === 'cinemas'">
            <optgroup v-for="city in cities" :key="city" :label="city">
              <option
                v-for="item in filters.cinemas.filter((i) => i.city === city)"
                :key="item.value"
                :value="item.value"
                :disabled="checkFilterValueDisabled(item, filterName)">
                {{ item.display }}
              </option>
            </optgroup>
          </template>

          <template v-else>
            <option
              v-for="filterOption in filter"
              :key="filterOption.value"
              :value="filterOption.value"
              :disabled="checkFilterValueDisabled(filterOption, filterName)">
              {{ filterOption.display }}
            </option>
          </template>
        </select>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { applyFilters } from '../../lib/filtering'
import useViewport from '../../features/viewport.js'
const { desktop } = useViewport()
// props

// emits
const emit = defineEmits(['filter-changed', 'update:modelValue'])

// reactive data
const filters = ref({
  cinemas: [],
  genres: [],
  roomTypes: [],
})
const filtersTemp = ref({
  cinemas: [],
  genres: [],
  roomTypes: [],
})
const selectedFiltersModel = ref({
  cinemas: 'All',
  genres: 'All',
  roomTypes: 'All',
})

const filtersDataFiltered = ref({})

const cities = computed(() => {
  if (!filters.value.cinemas || !filters.value.cinemas.length) {
    return []
  }

  return [...new Set(filters.value.cinemas.map((item) => item.city))].sort()
})

// hooks

onMounted(() => {
  assignFiltersData()
})

const props = defineProps({
  placeholderCinemas: {
    default: 'All',
    type: String,
  },
  placeholderGenres: {
    default: 'All',
    type: String,
  },
  placeholderRoomType: {
    default: 'All',
    type: String,
  },
  modelValue: {
    default() {
      return {
        cinemas: 'All',
        genres: 'All',
        roomTypes: 'All',
      }
    },
    type: Object,
  },
  showFilters: {
    default() {
      return ['cinemas', 'genres', 'roomTypes']
    },
    type: Array,
  },
  filtersData: {
    default() {
      return {}
    },
    type: Object,
    required: true,
  },
  containerClass: {
    default: 's-playing-now__options__filters',
    type: String,
  },
})

// methods
const assignFiltersData = () => {
  filters.value = {
    cinemas: [...props.filtersData.cinemas],
    genres: [...props.filtersData.genres],
    roomTypes: [...props.filtersData.roomTypes],
  }
  filtersTemp.value = { ...filters.value }

  filtersDataFiltered.value = { ...filters.value }

  const cinemaPreselected = preselectByFlag('cinemas')
  const genrePreselected = preselectByFlag('genres')

  if (cinemaPreselected || genrePreselected) {
    emit('filter-changed', selectedFiltersModel.value)
    emit('update:modelValue', selectedFiltersModel.value)
  }

  checkSingleValue()
}

// Preselects the first filter option flagged isSelected (set server-side from the
// CDP profile). Returns true if a selection was applied. Room types are never
// flagged, so this is effectively used for cinemas and genres.
const preselectByFlag = (filterName) => {
  const selected = filters.value[filterName].find((item) => item.isSelected)

  if (!selected) {
    return false
  }

  selectedFiltersModel.value[filterName] = selected.value
  setFiltersByKey(filterName)

  return true
}

const checkSingleValue = () => {
  if (
    filters.value.cinemas.length === 1 &&
    !props.showFilters.includes('cinemas')
  ) {
    makeFilterSelection(filters.value.cinemas[0].value, 'cinemas')
  }
  if (
    filters.value.genres.length === 1 &&
    !props.showFilters.includes('genres')
  ) {
    makeFilterSelection(filters.value.genres[0].value, 'genres')
  }
  if (
    filters.value.roomTypes.length === 1 &&
    !props.showFilters.includes('roomTypes')
  ) {
    makeFilterSelection(filters.value.roomTypes[0].value, 'roomTypes')
  }
}

const filterOptions = async (e, filterName) => {
  makeFilterSelection(e.target.value, filterName)
}

const makeFilterSelection = (val, filterName) => {
  selectedFiltersModel.value[filterName] = val

  setFiltersByKey(filterName)

  emit('filter-changed', selectedFiltersModel.value)
  emit('update:modelValue', selectedFiltersModel.value)
}

const setFiltersByKey = (key) => {
  if (selectedFiltersModel.value[key] !== 'All') {
    const selectedFilter = JSON.parse(
      JSON.stringify(
        filters.value[key].find(
          (item) => item.value === selectedFiltersModel.value[key],
        ),
      ),
    )

    filtersTemp.value[key] = JSON.parse(JSON.stringify(selectedFilter.filters))

    filtersDataFiltered.value = applyFilters(filters.value, filtersTemp.value)
  } else {
    filtersTemp.value[key] = undefined

    for (const filterKey in filtersTemp.value) {
      if (filterKey !== key) {
        filtersDataFiltered.value = applyFilters(
          filters.value,
          filtersTemp.value,
        )
      }
    }
  }
}
const checkFilterValueDisabled = (filter, filterName) => {
  if (filterName === 'cinemas') {
    return filter.isOnline === false
  }
  return filter.isDisabled === true
}

const equivalentPlaceholder = (filterName) => {
  if (filterName === 'cinemas') {
    return props.placeholderCinemas
  } else if (filterName === 'genres') {
    return props.placeholderGenres
  } else if (filterName === 'roomTypes') {
    return props.placeholderRoomType
  }
  return 'All'
}
</script>
